import React, { useState, useEffect } from "react";
import { Row, Col, Form } from "react-bootstrap";
import Card from "../../../components/Card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { FiEdit, FiPlus, FiTrash2 } from "react-icons/fi";
import { ROUTES, USER_ROLES, MODULE_ACL } from '../../../utilities/constant';
import { exportToCSV, hasPermission } from '../../../utilities/helpers';
import { useNavigate } from "react-router-dom";
import Loader from '../../../components/partials/components/loader';
import {
    API_MANAGE_LOCATIONS_LIST,
    API_MANAGE_LOCATIONS_UPDATE,
    API_MANAGE_LOCATIONS_DELETE,
} from "../../../service/url";
import { getApiCall } from "../../../service/getApiCall";
import { postApiCall } from "../../../service/postApiCall";
import { formatDateTime } from "../../../utilities/helpers";
import { getUser } from "../../../utilities/setting";
import exportData from '../../../utilities/export';
import { toast } from 'react-toastify';

const LocationListing = () => {
    const [locations, setLocations] = useState([]);
    const navigate = useNavigate();
    const [searchValue, setSearchValue] = useState("");
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [rows, setRows] = useState(5);
    const [loading, setLoading] = useState(false);
    const [isSuperAdmin, checkIsSuperAdmin] = useState(false);
    const [moduleOperations, setModuleOperations] = useState({});

    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editLocation, setEditLocation] = useState({ id: null, name: "" });

    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [locationToDelete, setLocationToDelete] = useState(null);
    const [bulkDeleteMode, setBulkDeleteMode] = useState(false);

    // ✅ Fetch locations
    const fetchLocations = async () => {
        setLoading(true);
        try {
            const data = await getApiCall(API_MANAGE_LOCATIONS_LIST, {});
            setLocations(data || []);
        } catch (error) {
            console.error("Error fetching Dialer locations:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const user = getUser();
            if (user) {
                if (user.role === USER_ROLES.SUPERADMIN) {
                    checkIsSuperAdmin(true);
                }
                if (user?.acl_settings) {
                    setModuleOperations(JSON.parse(user.acl_settings.module_operations));
                }
            }
    }, []);

    useEffect(() => {
        fetchLocations();
    }, []);

    // ✅ Filter locations for search
    const filteredLocations = locations.filter((loc) =>
        loc?.location_name.toLowerCase().includes(searchValue.toLowerCase())
    );

    // ✅ Checkbox logic
    const handleCheckboxToggle = (id) => {
        const isSelected = selectedLocations.includes(id);
        setSelectedLocations(
            isSelected
                ? selectedLocations.filter((i) => i !== id)
                : [...selectedLocations, id]
        );
    };

    const handleSelectAll = () => {
        if (selectedLocations.length === locations.length) {
            setSelectedLocations([]);
        } else {
            setSelectedLocations(locations.map((loc) => loc.id));
        }
    };

    // ✅ Delete
    const confirmDelete = (id) => {
        setLocationToDelete(id);
        setBulkDeleteMode(false);
        setDeleteModalVisible(true);
    };

    const confirmBulkDelete = () => {
        setBulkDeleteMode(true);
        setDeleteModalVisible(true);
    };

    const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] = useState({});

      useEffect(() => {
        const user = getUser();
          if (user) {
            if (user?.acl_settings) {
              setModuleOperationsForReadPermissionCheck(JSON.parse(user.acl_settings.module_operations));
            }
          }
      }, []);

      useEffect(() => {
        const onAclRefreshed = () => {
          const user = getUser();
          if (user?.acl_settings?.module_operations) {
            try {
              const parsed = typeof user.acl_settings.module_operations === "string"
                ? JSON.parse(user.acl_settings.module_operations)
                : user.acl_settings.module_operations;
              setModuleOperations(parsed);
              setModuleOperationsForReadPermissionCheck(parsed);
            } catch (e) {
              console.log("Failed to apply refreshed ACL", e);
            }
          }
        };
        window.addEventListener("acl-refreshed", onAclRefreshed);
        return () => window.removeEventListener("acl-refreshed", onAclRefreshed);
      }, []);

    const executeDelete = async () => {
        try {
            if (bulkDeleteMode) {
                const response = await Promise.all(
                    selectedLocations.map((id) =>
                        postApiCall(API_MANAGE_LOCATIONS_DELETE, { id })
                    )
                );
                if (response) {
                    toast.success("Dialer Locations deleted successfully");
                }
                setLocations(locations.filter((loc) => !selectedLocations.includes(loc.id)));
                setSelectedLocations([]);
            } else {
                const response = await postApiCall(API_MANAGE_LOCATIONS_DELETE, { id: locationToDelete });
                if (response) {
                    toast.success("Dialer Location deleted successfully");
                }
                setLocations(locations.filter((loc) => loc.id !== locationToDelete));
                setSelectedLocations(selectedLocations.filter((id) => id !== locationToDelete));
            }
        } catch (error) {
            toast.error(error.message || error || "Error deleting Dialer location(s)");
        } finally {
            setDeleteModalVisible(false);
            setLocationToDelete(null);
            setBulkDeleteMode(false);
            await fetchLocations();
        }
    };

    // ✅ Update
    const handleUpdate = async () => {
        try {
            const response = await postApiCall(API_MANAGE_LOCATIONS_UPDATE(editLocation.id), {
                location_name: editLocation.name,
            });
            if (response) {
                toast.success("Dialer Location updated successfully");
            }
            setEditModalVisible(false);
            await fetchLocations();
        } catch (error) {
            toast.error(error.message || error || "Error updating dialer location");
        }
    };

    // ✅ Action buttons
    const actionBodyTemplate = (rowData) => (
        <div style={{ display: "flex", gap: "10px" }}>
            {(isSuperAdmin || moduleOperations?.[MODULE_ACL.LOCATIONS]?.edit) && (
                <FiEdit
                    style={{ color: "#6c757d", cursor: "pointer", fontSize: "18px" }}
                    onClick={() => {
                        setEditLocation({ id: rowData.location_id, name: rowData.location_name });
                        setEditModalVisible(true);
                    }}
                />                
            )}
            {(isSuperAdmin || moduleOperations?.[MODULE_ACL.LOCATIONS]?.delete) && (
                <FiTrash2
                    style={{ color: "#dc3545", cursor: "pointer", fontSize: "18px" }}
                    onClick={() => confirmDelete(rowData.location_id)}
                />
            )}


        </div>
    );

    const rowClassName = (data) => ({
        "highlighted-row": selectedLocations.includes(data.id),
    });

    const renderEditFooter = () => (
        <div>
            <Button label="Cancel" className="p-button-text" onClick={() => setEditModalVisible(false)} />
            <Button label="Update" className="p-button-primary" onClick={handleUpdate} />
        </div>
    );

    const [showToast, setShowToast] = useState(false);
    const [toastProps, setToastProps] = useState(null);
    const [buttonDisable, setButtonDisable] = useState(false);

    const handleExport = async () => {
   try {
      setButtonDisable(true);

      exportToCSV({
         fileName: "location-list",
         data: filteredLocations,
         columns: [
            {
               header: "Location ID",
               accessor: "location_id"
            },
            {
               header: "Location Name",
               accessor: "location_name"
            },
            {
               header: "Created At",
               accessor: (row) =>
                  formatDateTime(row.created_at)
            }
         ]
      });

   } finally {
      setButtonDisable(false);
   }
};

    if (loading) {
        return <Loader />
    }

    return (
        <div>
            <Row>
                <Col sm="12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <div className="header-title">
                                <h4 className="card-title">Dialer Location List</h4>
                            </div>
                            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "flex-start" }}>
                                {(isSuperAdmin || moduleOperations?.[MODULE_ACL.LOCATIONS]?.add) && (<button className="btn btn-primary ml-auto" onClick={() => navigate(`/${ROUTES.MANAGE_LOCATIONS.CREATE}`)}>
                                    <FiPlus className="me-1" />
                                    Create Dialer Location</button>)}
                                {locations.length > 0 &&
                                    <Button
                                        label="Export Data"
                                        icon="pi pi-file-export"
                                        onClick={handleExport}
                                        className="p-button-active p-button-sm"
                                        style={{ marginBottom: '10px', marginLeft: '10px' }}
                                        disabled={buttonDisable}
                                    >
                                        &nbsp;
                                        {buttonDisable && (
                                            <span className="spinner-border spinner-border-sm ml-2" role="status" aria-hidden="true"></span>
                                        )}
                                    </Button>
                                }
                            </div>
                        </Card.Header>

                        <Card.Body className="px-0">
                            <div className="table-responsive border-bottom my-3">
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px" }}>
                                    {((isSuperAdmin || moduleOperations?.[MODULE_ACL.LOCATIONS]?.delete)) && (
                                        <Button
                                            label="Delete Selected"
                                            className="p-button-danger p-button-sm"
                                            disabled={selectedLocations.length === 0}
                                            onClick={confirmBulkDelete}
                                        />
                                    )}
                                    <Form.Group>
                                        <Form.Control
                                            style={{ marginLeft: "10px", height: "45px", border: "1px solid #ced4da", fontSize: "15px" }}
                                            placeholder="Search by Dialer Location Name..."
                                            type="text"
                                            value={searchValue}
                                            onChange={(e) => setSearchValue(e.target.value)}
                                        />
                                    </Form.Group>

                                    <div style={{ display: "flex", alignItems: "center", gap: "5px", marginLeft: "20px" }}>
                                        <span>Show</span>
                                        <Form.Select
  value={rows}
  onChange={(e) => setRows(Number(e.target.value))}
  style={{
    width: "100px",
    height: "40px",
    alignItems: "center",
  }}
>
  <option value="">Select Rows</option>

  {[
    { label: "5", value: 5 },
    { label: "10", value: 10 },
    { label: "20", value: 20 },
    { label: "50", value: 50 },
  ].map((option) => (
    <option
      key={option.value}
      value={option.value}
    >
      {option.label}
    </option>
  ))}
</Form.Select>
                                        <span>items</span>
                                    </div>
                                </div>

                                <div className="scroll-container">
                                    <DataTable
                                        value={filteredLocations}
                                        tableStyle={{ minWidth: "50rem" }}
                                        paginator
                                        rows={rows}
                                        rowClassName={rowClassName}
                                        emptyMessage="No Dialer locations found"
                                    >
                                        <Column
                                            header={
                                                <Checkbox
                                                    checked={selectedLocations.length === locations.length && locations.length > 0}
                                                    onChange={handleSelectAll}
                                                />
                                            }
                                            headerStyle={{ width: "3rem" }}
                                            body={(rowData) => (
                                                <Checkbox
                                                    checked={selectedLocations.includes(rowData.location_id)}
                                                    onChange={() => handleCheckboxToggle(rowData.location_id)}
                                                />
                                            )}
                                        />
                                        <Column field="location_id" header="DialerLocation ID" sortable />
                                        <Column field="location_name" header="Dialer Location Name" sortable />
                                        <Column field="created_at" header="Created At" body={(rowData) => formatDateTime(rowData.created_at)} sortable />
                                        <Column header="Action" body={actionBodyTemplate} />
                                    </DataTable>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Edit Modal */}
            <Dialog
                header="Update Dialer Location"
                visible={editModalVisible}
                style={{ width: "30vw" }}
                footer={renderEditFooter()}
                onHide={() => setEditModalVisible(false)}
            >
                <Form.Group>
                    <Form.Label>Dialer Location Name <span style={{ color: "red", margin: "0 4px" }}>*</span></Form.Label>
                    <Form.Control
                        type="text"
                        value={editLocation.name}
                       onChange={(e) => {
                  const val = e.target.value;
                  if (/^[A-Za-z\s]*$/.test(val)) {
                  setEditLocation({ ...editLocation, name: val });
                 }
                  }}
                    />
                </Form.Group>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog
                header="Confirm Delete"
                visible={deleteModalVisible}
                style={{ width: "25vw" }}
                onHide={() => setDeleteModalVisible(false)}
                footer={
                    <div>
                        <Button label="Cancel" className="p-button-text" onClick={() => setDeleteModalVisible(false)} />
                        <Button label="Delete" className="p-button-danger" onClick={executeDelete} />
                    </div>
                }
            >
                <p>
                    Are you sure you want to delete {bulkDeleteMode ? "the selected Dialer locations" : "this Dialer location"}?
                </p>
            </Dialog>
        </div>
    );
};

export default LocationListing;
